import type { GradientMesh, GradientData } from '@wix/css-gradient-generator';
import { type ColorLayerData } from '#packages/util';
import { type Style } from '@wix/document-services-types';
import _ from 'lodash';
import type { CompRef } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import { supportedGradientSkins } from './constants';

type Gradient = GradientData | GradientMesh;
interface BackgroundData {
  type: 'BackgroundMedia';
  colorLayers?: ColorLayerData[];
  colorOverlay?: string;
  colorOverlayOpacity?: number;
  color?: string;
  colorOpacity?: number;
}

export interface ColorPickerValues {
  color: string | Gradient;
  alpha: number;
}

const createColorLayer = (
  fill: ColorLayerData['fill'],
  opacity: number = 1,
): ColorLayerData => {
  const type = `${fill.type}Layer` as ColorLayerData['type'];
  return { type, opacity, fill };
};

const getUpdatedData = <T extends object>(
  newData: T,
  fill: ColorLayerData['fill'],
  opacity: number,
  path: string,
): T => {
  const colorLayer = createColorLayer(fill, opacity);
  _.set(newData, path, [colorLayer]);
  return newData;
};

export const getOpacityValue = (data: BackgroundData): number => {
  return data.colorLayers?.[0]?.opacity ?? data.colorOpacity ?? 1;
};

export const getColorValue = (data: BackgroundData) => {
  const fill = data.colorLayers?.[0].fill || data.color;
  if (typeof fill === 'string') {
    return fill;
  }
  return fill?.type === 'SolidColor' ? fill.color : fill;
};

export const getUpdatedDesignData = (
  data: BackgroundData,
  { color, alpha: opacity }: ColorPickerValues,
) => {
  opacity = opacity ?? getOpacityValue(data);
  const newData = _.cloneDeep(data);
  if (typeof color === 'string') {
    return getUpdatedData(
      newData,
      { type: 'SolidColor', color: color ?? '#FFFFFF' },
      opacity,
      'colorLayers',
    );
  }

  if ((color as Gradient)?.type) {
    return getUpdatedData(newData, color as Gradient, opacity, 'colorLayers');
  }

  if (Array.isArray(color)) {
    return getUpdatedData(
      newData,
      {
        type: 'GradientMesh',
        gradients: color as GradientMesh['gradients'],
      },
      opacity,
      'colorLayers',
    );
  }

  return data;
};

export const getStyleFill = (compStyle: Style) => {
  return (
    compStyle.background?.gradient?.[0]?.fill ??
    compStyle?.properties?.bg ??
    '#FFFFFF'
  );
};

export const getStyleOpacity = (compStyle: Style) => {
  return (
    compStyle.background?.gradient?.[0]?.opacity ??
    compStyle?.properties?.['alpha-bg'] ??
    1
  );
};

export const getUpdatedStyleData = (
  compStyle: Style,
  { color: fill, alpha: opacity }: ColorPickerValues,
) => {
  opacity = opacity ?? getStyleOpacity(compStyle);
  const newData = _.cloneDeep(compStyle);

  if (typeof fill === 'string') {
    newData.properties.bg = fill || 'color_11';
    newData.properties['alpha-bg'] = opacity.toString();
    if (newData.background && newData.background.gradient?.[0]) {
      _.set(newData, 'background.gradient', []);
    }
    return newData;
  }

  if (newData.properties) {
    newData.properties.bg = 'transparent';
  }

  if ((fill as Gradient)?.type) {
    return getUpdatedData(
      newData,
      fill as Gradient,
      opacity,
      'background.gradient',
    );
  }

  if (Array.isArray(fill)) {
    return getUpdatedData(
      newData,
      {
        type: 'GradientMesh',
        gradients: fill as GradientMesh['gradients'],
      },
      opacity,
      'background.gradient',
    );
  }
};

export const areGradientSupportedComponents = (
  editorAPI: EditorAPI,
  selectedComponents: CompRef | CompRef[],
  selectedSkin: string,
) => {
  const isMultiSelect = Array.isArray(selectedComponents);

  return isMultiSelect
    ? selectedComponents.every((comp: CompRef) => {
        const skin = editorAPI.components.style.get(comp).skin;
        return supportedGradientSkins[skin] === true;
      })
    : supportedGradientSkins[selectedSkin] === true;
};
